// ---------------------------------------------------------------------------
// BellGrid — the week × period matrix. Each cell renders the bell's strike
// count as tally marks and colours itself by status (played/late/failed/
// offline/pending/holiday). Cells are clickable to open the detail drawer.
// ---------------------------------------------------------------------------
import React from "react";
import {FILTERABLE_STATUSES} from "../constants/BellStatusConstants";
import {minutes, nowMinutes, parseISO, statusMeta, todayISO} from "../utils/bellStatusUtils";
import Legend from "./Legend";

const WarnIcon = () => (
    <span className="warn">
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M7 1.8l5.4 9.4H1.6L7 1.8z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
            <path d="M7 5.6v2.6M7 9.9h.01" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
    </span>
);

function Tally({n, cap = 8, num}) {
    if (!n) {
        return <span className="tally"><i /></span>;
    }
    const shown = Math.min(n, cap);
    return (
        <span className="tally">
            {Array.from({length: shown}).map((_, i) => (
                <i key={i} />
            ))}
            {n > cap ? <span className="more">+{num(n - cap)}</span> : null}
        </span>
    );
}

function FilterChips({i18n, filter, onToggle}) {
    const labels = {
        played: {en: "Played", ne: "बज्यो", color: "var(--emerald)"},
        late: {en: "Late", ne: "ढिलो", color: "var(--gold)"},
        failed: {en: "Failed", ne: "असफल", color: "var(--fail)"},
        offline: {en: "Offline", ne: "अफलाइन", color: "var(--offline)"},
    };
    return (
        <div className="filters">
            {FILTERABLE_STATUSES.map((f) => (
                <button
                    key={f}
                    className="fchip"
                    aria-pressed={filter.has(f)}
                    onClick={() => onToggle(f)}
                >
                    <i className="sw" style={{background: labels[f].color}} />
                    <span className="en">{labels[f].en}</span>
                    <span className="ne">{labels[f].ne}</span>
                </button>
            ))}
        </div>
    );
}

function SkeletonGrid() {
    return (
        <div className="bellgrid">
            <div className="gh corner" />
            {Array.from({length: 7}).map((_, i) => (
                <div className="gh" key={"h" + i}>
                    <div className="skel" style={{height: "14px", width: "66%"}} />
                    <div className="skel" style={{height: "10px", width: "44%", marginTop: "6px"}} />
                </div>
            ))}
            {Array.from({length: 8}).map((_, r) => (
                <React.Fragment key={"r" + r}>
                    <div className="rowlabel">
                        <div className="skel" style={{height: "13px", width: "70%"}} />
                    </div>
                    {Array.from({length: 7}).map((_, i) => (
                        <div className="cell" key={"c" + r + i}>
                            <div className="skel" style={{height: "15px", width: "38px"}} />
                            <div className="skel" style={{height: "11px", width: "46px"}} />
                        </div>
                    ))}
                </React.Fragment>
            ))}
        </div>
    );
}

function DayHeader({day, i18n}) {
    const {num, mon, days: dayNames, t} = i18n;
    const dt = parseISO(day.date);
    const isToday = day.date === todayISO();
    const ev = (day.events || []).filter((e) => e.status !== "pending");
    const ok = ev.filter((e) => e.status === "played" || e.status === "late").length;
    const bad = ev.filter((e) => e.status === "failed").length;
    const off = ev.filter((e) => e.status === "offline").length;

    return (
        <div className={`gh ${isToday ? "today" : ""} ${day.holiday ? "holiday" : ""}`}>
            <div className="dw">{dayNames[dt.getDay()]}</div>
            <div className="dd">
                {day.bs ? day.bs : `${num(dt.getDate())} ${mon(dt.getMonth())}`}
                {isToday ? ` · ${t("today")}` : ""}
            </div>
            {day.holiday ? (
                <span className="hpill">{day.holiday_name || t("holiday")}</span>
            ) : (
                <div className="daybar">
                    {ok ? <i style={{background: "var(--emerald)", flex: ok}} /> : null}
                    {bad ? <i style={{background: "var(--fail)", flex: bad}} /> : null}
                    {off ? <i style={{background: "var(--offline)", flex: off}} /> : null}
                </div>
            )}
        </div>
    );
}

function BellCell({day, period, rowIndex, i18n, filter, onOpen}) {
    const {num, t, days: dayNames} = i18n;
    const e = (day.events || []).find((x) => x.period_id === period.id);
    const st = day.holiday ? "holiday" : e ? e.status : "skipped";
    const isNow =
        day.date === todayISO() && e && e.status !== "pending" &&
        Math.abs(minutes(period.time) - nowMinutes()) <= 1;

    let center = "", note = "";
    if (st === "holiday" || st === "skipped") center = "—";
    else if (st === "played") center = num(e.actual_at || period.time);
    else if (st === "late") {
        center = num(e.actual_at || period.time);
        note = `+${num(e.delay_sec)}${i18n.lang === "ne" ? " से." : "s"}`;
    } else if (st === "failed") {
        center = num(period.time);
        note = (e && e.error_code) || "";
    } else if (st === "offline") {
        center = num(period.time);
        note = t("noSignal");
    } else center = num(period.time);

    const marks = st === "played" || st === "late" ? e.strikes_played || period.strikes : period.strikes;
    const clickable = st !== "holiday" && st !== "skipped";
    const dim = filter.size > 0 && !filter.has(st);
    const title = `${i18n.lang === "ne" && period.name_ne ? period.name_ne : period.name} · ${dayNames[parseISO(day.date).getDay()]} · ${statusMeta(st, t).label}`;

    const inner = (
        <>
            <Tally n={marks} num={num} />
            <span className="ctime">
                {center}
                {note ? <span className="cnote">{note}</span> : null}
            </span>
            {isNow ? <span className="livedot" /> : null}
            {st === "failed" ? <WarnIcon /> : null}
        </>
    );

    const className = `cell s-${st} ${isNow ? "now" : ""} ${dim ? "dim" : ""} rise`;
    const style = {animationDelay: `${Math.min(rowIndex * 22, 260)}ms`};

    if (clickable) {
        return (
            <button className={className} style={style} title={title} onClick={() => onOpen(day.date, period.id)}>
                {inner}
            </button>
        );
    }
    return (
        <div className={className} style={style} title={title}>
            {inner}
        </div>
    );
}

function BellGrid({data, i18n, filter, onToggleFilter, onOpen, loading, gridSubLabel}) {
    const {t} = i18n;
    const hasDays = data && data.days && data.days.length;

    return (
        <section className="card">
            <div className="card-head">
                <div className="card-title">
                    <span className="en" style={{fontFamily: "var(--display)", color: "var(--ink)", fontSize: "17px", margin: 0}}>
                        Bell ledger
                    </span>
                    <span className="ne" style={{fontFamily: "var(--display)", color: "var(--ink)", fontSize: "17px", margin: 0}}>
                        घण्टी अभिलेख
                    </span>
                    <span>{gridSubLabel}</span>
                </div>
                <FilterChips i18n={i18n} filter={filter} onToggle={onToggleFilter} />
            </div>

            <div className="gridscroll">
                {loading ? (
                    <SkeletonGrid />
                ) : hasDays ? (
                    <div className="bellgrid">
                        <div className="gh corner">
                            <div className="ck">{t("period")}</div>
                        </div>
                        {data.days.map((d) => (
                            <DayHeader key={d.date} day={d} i18n={i18n} />
                        ))}
                        {(data.periods || []).map((p, pi) => (
                            <React.Fragment key={p.id}>
                                <div className={`rowlabel ${["break", "assembly", "dismissal"].includes(p.kind) ? "break" : ""}`}>
                                    <div className="pn">{i18n.lang === "ne" && p.name_ne ? p.name_ne : p.name}</div>
                                    <div className="pt">
                                        {i18n.num(p.time)} <span className="strikes">{i18n.num(p.strikes)}×</span>
                                    </div>
                                </div>
                                {data.days.map((d) => (
                                    <BellCell
                                        key={d.date + p.id}
                                        day={d}
                                        period={p}
                                        rowIndex={pi}
                                        i18n={i18n}
                                        filter={filter}
                                        onOpen={onOpen}
                                    />
                                ))}
                            </React.Fragment>
                        ))}
                    </div>
                ) : null}
            </div>

            {!loading && !hasDays ? (
                <div className="state">
                    <div className="st">{t("emptyT")}</div>
                    <div>{t("emptyB")}</div>
                </div>
            ) : null}

            <Legend />
        </section>
    );
}

export default BellGrid;
